import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { 
  Loader2, ArrowLeft, AlertCircle, Store, Clock, Truck, 
  ImageIcon, BookOpen, BarChart3, ChevronRight, ExternalLink,
  Check
} from "lucide-react";
import { Restaurant } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import RestaurantFormGeneral, { RestaurantFormGeneralRef } from "@/components/restaurant/RestaurantFormGeneral";
import RestaurantFormOpeningHours from "@/components/restaurant/RestaurantFormOpeningHours";
import RestaurantFormMedia from "@/components/restaurant/RestaurantFormMedia";
import RestaurantFormMenus from "@/components/restaurant/RestaurantFormMenus";
import RestaurantFormDelivery from "@/components/restaurant/RestaurantFormDelivery";
import RestaurantInsightsTab from "@/components/restaurant/RestaurantInsightsTab";
import { useToast } from "@/hooks/use-toast";
import { normalizeImagePath } from "@/lib/utils";

const TABS = [
  { id: "allgemein", label: "Allgemein", mobileLabel: "Info", icon: Store, color: "text-emerald-600" },
  { id: "oeffnungszeiten", label: "Öffnungszeiten", mobileLabel: "Zeiten", icon: Clock, color: "text-blue-600" },
  { id: "lieferung", label: "Lieferung", mobileLabel: "Lieferung", icon: Truck, color: "text-orange-600" },
  { id: "bilder", label: "Bilder", mobileLabel: "Bilder", icon: ImageIcon, color: "text-purple-600" },
  { id: "speisekarten", label: "Speisekarten", mobileLabel: "Menüs", icon: BookOpen, color: "text-pink-600" },
  { id: "insights", label: "Insights", mobileLabel: "Stats", icon: BarChart3, color: "text-cyan-600" },
] as const;

const RestaurantEditPage = () => {
  const { id } = useParams();
  const [, navigate] = useLocation();
  const [error, setError] = useState<string | null>(null);
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get('tab') || 'allgemein';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const formGeneralRef = useRef<RestaurantFormGeneralRef>(null);

  const { data: restaurant, isLoading, isError, error: queryError } = useQuery<Restaurant>({
    queryKey: [`/api/restaurants/${id}`],
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  const processedRestaurant = useMemo(() => {
    if (!restaurant) return null;
    const processed = {...restaurant};
    if (processed.categories && processed.categories.length > 0) {
      processed.cuisineId = processed.categories[0].id;
    }
    return processed;
  }, [restaurant]);

  const mainImage = useMemo(() => {
    if (!restaurant?.images || restaurant.images.length === 0) return null;
    const main = restaurant.images.find((img: any) => img.sortOrder === 1);
    return main || restaurant.images[0];
  }, [restaurant]);

  useEffect(() => {
    if (isError) setError("Das Restaurant konnte nicht geladen werden.");
    else setError(null);
  }, [isError, queryError, id]);

  const handleTabChange = (newTab: string) => {
    if (!isDirty || isSaving) { setActiveTab(newTab); return; }
    if (window.confirm("Ungespeicherte Änderungen gehen verloren.\nTab wirklich wechseln?")) {
      setIsDirty(false);
      setActiveTab(newTab);
    }
  };

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty && !isSaving) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, isSaving]);

  if (isLoading) return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
      <div className="text-center">
        <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
        <p className="mt-4 text-sm text-slate-500 font-medium">Restaurant wird geladen…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>
        <Button onClick={() => navigate("/admin")} variant="outline" className="mt-4 w-full"><ArrowLeft className="mr-2 h-4 w-4" /> Zurück</Button>
      </div>
    </div>
  );

  if (!restaurant) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50/80 to-white">
      {/* === PREMIUM HEADER === */}
      <header className="relative bg-white border-b border-slate-200/60">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(99,102,241,0.03),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 pt-3 sm:pt-4 pb-1 sm:pb-2">
            <nav className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-slate-500 min-w-0">
              <Link href="/admin" className="hover:text-primary transition-colors flex-shrink-0">Dashboard</Link>
              <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
              <Link href="/admin" className="hover:text-primary transition-colors flex-shrink-0">Restaurants</Link>
              <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
              <span className="text-slate-900 font-medium truncate">{restaurant.name}</span>
            </nav>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {restaurant.slug && (
                <Link href={`/restaurants/${restaurant.slug}`}>
                  <Button variant="ghost" size="sm" className="text-xs text-slate-500 hover:text-primary gap-1 hidden sm:flex h-8">
                    <ExternalLink className="h-3.5 w-3.5" /> Vorschau
                  </Button>
                </Link>
              )}
              <Button onClick={() => navigate("/admin")} variant="ghost" size="sm" className="gap-1 h-8 text-xs sm:text-sm">
                <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Zurück</span>
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-4 px-4 sm:px-6 lg:px-8 pb-3 sm:pb-4">
            <div className="relative flex-shrink-0">
              {mainImage ? (
                <img src={normalizeImagePath((mainImage as any).path)} alt={restaurant.name}
                  className="h-12 w-12 sm:h-16 sm:w-16 rounded-xl object-cover ring-2 ring-white shadow-md"
                  onError={(e) => { (e.target as HTMLImageElement).src = '/placeholder_header.png'; }} />
              ) : (
                <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center ring-2 ring-white shadow-md">
                  <Store className="h-5 w-5 sm:h-6 sm:w-6 text-primary/60" />
                </div>
              )}
              <div className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 sm:h-4 sm:w-4 rounded-full border-2 border-white ${restaurant.enabled ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <h1 className="text-base sm:text-xl font-bold text-slate-900 truncate">{restaurant.name}</h1>
                <Badge variant={restaurant.enabled ? "default" : "secondary"} className={`text-[10px] px-1.5 py-0 h-4 sm:h-5 font-semibold ${restaurant.enabled ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}`}>
                  {restaurant.enabled ? "Aktiv" : "Inaktiv"}
                </Badge>
                {restaurant.delivery && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 sm:h-5 font-semibold border-orange-200 text-orange-600 bg-orange-50 hidden xs:flex">
                    <Truck className="h-2.5 w-2.5 mr-0.5" /> Lieferung
                  </Badge>
                )}
              </div>
              <p className="text-xs sm:text-sm text-slate-500 truncate mt-0.5">
                {[restaurant.street, restaurant.houseNumber].filter(Boolean).join(' ')}
                {restaurant.city && ` · ${restaurant.postalCode} ${restaurant.city}`}
              </p>
            </div>
            {isDirty && !isSaving && (
              <div className="hidden sm:flex items-center gap-1.5 bg-amber-50 text-amber-700 text-xs font-medium px-3 py-1.5 rounded-full border border-amber-200">
                <div className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" /> Ungespeichert
              </div>
            )}
          </div>
        </div>
      </header>

      {/* === TAB NAVIGATION === */}
      <div className="sticky top-14 lg:top-16 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <nav className="flex gap-0 sm:gap-0.5 overflow-x-auto scrollbar-hide -mb-px py-0.5 sm:py-1" role="tablist">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => handleTabChange(tab.id)} role="tab" aria-selected={isActive}
                  className={`relative flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-t-lg flex-shrink-0 outline-none ${isActive ? 'text-primary' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
                  <Icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${isActive ? tab.color : ''}`} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.mobileLabel}</span>
                  {isActive && <span className="absolute bottom-0 left-1 sm:left-2 right-1 sm:right-2 h-0.5 bg-primary rounded-full" />}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* === CONTENT === */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 pb-32 md:pb-8">
        {isSaving && (
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-6 flex items-center gap-3 sm:gap-4 mx-4">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Wird gespeichert…</p>
                <p className="text-sm text-slate-500">Änderungen werden übernommen</p>
              </div>
            </div>
          </div>
        )}

        {isDirty && !isSaving && (
          <div className="sm:hidden flex items-center gap-1.5 bg-amber-50 text-amber-700 text-xs font-medium px-3 py-2 rounded-lg border border-amber-200 mb-3">
            <div className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" /> Ungespeicherte Änderungen
          </div>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="hidden">
            {TABS.map(tab => <TabsTrigger key={tab.id} value={tab.id}>{tab.label}</TabsTrigger>)}
          </TabsList>
          <TabsContent value="allgemein" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
            <RestaurantFormGeneral ref={formGeneralRef} restaurant={processedRestaurant!} onDirtyChange={setIsDirty} onSavingChange={setIsSaving} />
          </TabsContent>
          <TabsContent value="oeffnungszeiten" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
            <RestaurantFormOpeningHours restaurant={processedRestaurant!} onDirtyChange={setIsDirty} onSavingChange={setIsSaving} />
          </TabsContent>
          <TabsContent value="lieferung" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
            <RestaurantFormDelivery restaurant={processedRestaurant!} onDirtyChange={setIsDirty} onSavingChange={setIsSaving} />
          </TabsContent>
          <TabsContent value="bilder" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
            <RestaurantFormMedia restaurant={processedRestaurant!} onDirtyChange={setIsDirty} onSavingChange={setIsSaving} />
          </TabsContent>
          <TabsContent value="speisekarten" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
            <RestaurantFormMenus restaurant={processedRestaurant!} onDirtyChange={setIsDirty} onSavingChange={setIsSaving} />
          </TabsContent>
          <TabsContent value="insights" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
            <RestaurantInsightsTab restaurant={processedRestaurant!} />
          </TabsContent>
        </Tabs>
      </main>

      {/* === MOBILE BOTTOM BAR === */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40">
        <div className="h-4 bg-gradient-to-t from-white to-transparent pointer-events-none" />
        <div className="bg-white/95 backdrop-blur-md border-t border-slate-200/60 px-4 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2">
          <div className="flex gap-2 max-w-lg mx-auto">
            <Button onClick={() => navigate("/admin")} variant="ghost" size="sm" className="flex-1 text-slate-600 h-9" disabled={isSaving}>
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Zurück
            </Button>
            {activeTab === 'allgemein' && (
              <Button
                onClick={async () => {
                  if (!isDirty) return;
                  setIsSaving(true);
                  try { if (formGeneralRef.current) await formGeneralRef.current.save(); }
                  catch (err) { toast({ title: "Fehler", description: err instanceof Error ? err.message : "Speichern fehlgeschlagen", variant: "destructive" }); }
                  finally { setIsSaving(false); }
                }}
                className="flex-[2] bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 h-9" disabled={isSaving || !isDirty} size="sm">
                {isSaving ? (<><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Speichern…</>) : isDirty ? (<><Check className="h-3.5 w-3.5 mr-1.5" />Speichern</>) : (<><Check className="h-3.5 w-3.5 mr-1.5" />Gespeichert</>)}
              </Button>
            )}
            {(activeTab === 'oeffnungszeiten' || activeTab === 'lieferung') && (
              <Button
                type="submit"
                form={activeTab === 'oeffnungszeiten' ? 'opening-hours-form' : 'delivery-form'}
                className="flex-[2] bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 h-9" disabled={isSaving || !isDirty} size="sm">
                {isSaving ? (<><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Speichern…</>) : isDirty ? (<><Check className="h-3.5 w-3.5 mr-1.5" />Speichern</>) : (<><Check className="h-3.5 w-3.5 mr-1.5" />Gespeichert</>)}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantEditPage;
